<script setup lang="ts">
import { CButton } from '@/shared/ui'

interface Props {
  title: string
  description: string
  featuresTitle: string
  features: string[]
  price: string
  originalPrice?: string | null
  discount?: string | null
  isCurrent?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  currentStatus?: string
  buttonText: string
}

defineProps<Props>()
const emit = defineEmits<{
  select: []
}>()
</script>

<template>
  <div
    class="pricing-card bg-white rounded-lg p-6 shadow-sm border border-border-lighter flex flex-col items-center"
  >
    <!-- Title -->
    <h3 class="text-[32px] font-bold text-primary mb-2.5">
      {{ title }}
    </h3>

    <!-- Description -->
    <p class="text-text-regular text-sm leading-5 mb-4 text-center font-normal">
      {{ description }}
    </p>

    <!-- Features Section -->
    <div class="bg-[#F0F9EB] rounded-xl p-4 mb-4">
      <h4 class="font-semibold text-text-primary text-base leading-5 mb-2">
        {{ featuresTitle }}
      </h4>
      <ul class="space-y-2">
        <li
          v-for="feature in features"
          :key="feature"
          class="flex items-center gap-2.5"
        >
          <div
            class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 border border-[#606266]"
          >
            <i class="icon-tick text-[#606266]" />
          </div>
          <span class="text-sm text-text-regular font-medium leading-5">{{
            feature
          }}</span>
        </li>
      </ul>
    </div>

    <!-- Price -->
    <div class="my-9 text-center">
      <div
        v-if="originalPrice"
        class="flex items-center justify-center gap-2 mb-1"
      >
        <span class="text-lg text-text-secondary line-through">
          {{ originalPrice }}
        </span>
        <span
          class="bg-[#67C23A] text-white text-xs font-medium px-2 py-0.5 rounded"
        >
          -{{ discount }}
        </span>
      </div>
      <h2 class="text-2xl leading-7 font-bold text-blue-light-3">
        {{ price }}
      </h2>
    </div>

    <!-- Current Status (if applicable) -->
    <div
      v-if="currentStatus"
      class="bg-primary-light-9 border border-[#C6E2FF] rounded-lg py-4 px-5 mb-4 w-full"
    >
      <div class="flex items-center gap-2">
        <img
          src="@/shared/assets/svg/info-filled.svg"
          alt="current-status"
          class="w-4 h-4"
        >
        <span class="text-xs font-medium leading-5 text-blue-light-2">{{
          currentStatus
        }}</span>
      </div>
    </div>

    <!-- Action Button -->

    <CButton
      :disabled="isCurrent || isDisabled"
      :loading="isLoading"
      class="w-full mt-auto disabled:text-[#909399] disabled:hover:text-[#909399]"
      type="primary"
      @click="emit('select')"
    >
      {{ buttonText }}
    </CButton>
  </div>
</template>

<style scoped>
.pricing-card {
  transition:
    transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;
}

.pricing-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}
</style>
